import { Table } from '@bphxd/ds-core-react';
import { Fragment, useMemo, useState } from 'react';
import * as TanstackTable from '@tanstack/react-table';
import { Edit16 } from '@bphxd/ds-core-react/lib/icons';
import classes from './JobTasksTable.module.scss';
import { TPermit, TPermitWithJobDetails, TSapMo } from '../../../lib/types';
import TaskPermitForm from '../TaskPermitForm/TaskPermitForm';
import { useMutation, useQuery } from '@apollo/client';
import { GET_PERMITS_BY_TASK_IDS, UPSERT_TASK_PERMIT } from '../../../graph-queries/tar/job';
import { FormModal } from '../../molecules/FormModal/FormModal';
import PermitDetailsModal from '../../pages/Permits/PermitDetailsModal';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import clsx from 'clsx';
import { useHasUserAccess } from '../../../lib/util/auth';
import {
  PermitRequirementEnum,
  PermitWalkedEnum,
} from '../../../graph-queries/__generated__/graphql';
import { Alert16, Check16 } from '@bphxd/ds-core-react/lib/icons';

type Props = {
  tasks: TSapMo['labors'];
  moNumber: string;
};

function JobTasksTable({ tasks, moNumber }: Props) {
  const hasUserAccess = useHasUserAccess();
  const notice = useNoticeBar();
  const [selectedPermit, setSelectedPermit] = useState<TPermitWithJobDetails | null>(null);
  const handleModalClose = () => setSelectedPermit(null);

  const { data: permits, loading } = useQuery(GET_PERMITS_BY_TASK_IDS, {
    variables: { taskIds: tasks?.map((t) => t.taskId) || [], moNumber },
  });

  const [upsertTaskPermit] = useMutation(UPSERT_TASK_PERMIT, {
    refetchQueries: [GET_PERMITS_BY_TASK_IDS],
  });

  const data = useMemo(
    () =>
      tasks?.map((task, i) => ({
        ...task,
        permit: {
          ...((permits?.permitsByTaskIds.find(
            (permit: { taskId: number }) => permit.taskId === task.taskId
          ) as unknown as TPermitWithJobDetails) || undefined),
          taskDescription: task.operationDescription,
        },
      })) || [],
    [permits, tasks]
  );

  const table = TanstackTable.useReactTable({
    data,
    columns: [
      {
        header: 'Task Number',
        accessorKey: 'taskId',
        enableSorting: false,
      },
      {
        header: 'Description',
        accessorKey: 'operationDescription',
        enableSorting: false,
      },
      {
        header: 'Timing',
        accessorKey: 'permit.timing',
        enableSorting: false,
      },
      {
        header: 'Permit Requirement',
        accessorKey: 'permit.permitRequirement',
        enableSorting: false,
      },
      {
        header: 'Permit Number',
        accessorKey: 'permit.permitNumber',
        enableSorting: false,
        cell: (context) => {
          const permitNumber = context.getValue() as string;
          if (permitNumber) {
            const formattedPermitNumber = permitNumber.replace('#', '%23');
            const url = `https://ecow.bpglobal.com/permitvision/permitvision/editor/certificate/${formattedPermitNumber}?openerRegistrationNumber=Permit`;

            return (
              <a href={url} target="_blank" rel="noreferrer">
                {permitNumber}
              </a>
            );
          }
        },
      },
      {
        header: 'Permit Status',
        accessorKey: 'permit.ecowPermit.statusComment',
        enableSorting: false,
      },

      {
        header: 'Permit Owner',
        accessorKey: 'permit.permitOwner',
        enableSorting: false,
      },
      {
        header: 'Integrity Check',
        enableSorting: false,
        cell: ({ row }) => {
          if (!row.original.permit.permitNumber) {
            return;
          }

          return row.original.permit.lastKnownTaskDescription ===
            row.original.operationDescription ? (
            <Check16 />
          ) : (
            <Alert16
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedPermit(row.original.permit)}
            />
          );
        },
      },
    ],
    getCoreRowModel: TanstackTable.getCoreRowModel(),
    getFilteredRowModel: TanstackTable.getFilteredRowModel(),
    getSortedRowModel: TanstackTable.getSortedRowModel(),
    /*Pagination not currently requried for task table
    getPaginationRowModel: TanstackTable.getPaginationRowModel(),*/
  });

  const renderEditForm = (
    onClose: () => void,
    task: TSapMo['labors'][0] & { permit?: TPermit }
  ) => {
    return (
      <TaskPermitForm
        defaultValues={{
          ...task.permit,
          taskId: task.taskId,
          permitNumber: task.permit?.permitNumber || '',
          permitOwner: task.permit?.permitOwner || '',
          permitRequirement: task.permit?.permitRequirement || PermitRequirementEnum.Required,
          moNumber,
          permitWalked: task.permit?.permitWalked || PermitWalkedEnum.Required,
          comments: task.permit?.comments || '',
        }}
        taskDescription={task.operationDescription || ''}
        onSubmit={async (taskPermit) => {
          taskPermit.moNumber = moNumber;
          return upsertTaskPermit({ variables: { input: taskPermit } }).then(() => {
            notice.sendSuccess('Task details saved');
          });
        }}
        onClose={onClose}
      />
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <h5>Task List</h5>
      </div>
      <Table
        size="sm"
        allSelected={table.getIsAllRowsSelected()}
        onSelectAll={() => table.toggleAllRowsSelected()}
        someSelected={table.getIsSomeRowsSelected()}
        className={classes.hideSelectionColumn}
      >
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.HeaderRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Fragment key={header.id}>
                  {header.isPlaceholder ? (
                    <Table.Header />
                  ) : header.column.getCanSort() ? (
                    <Table.Header
                      onSort={(currentSort) =>
                        header.column.toggleSorting(
                          currentSort === 'desc' ? undefined : currentSort === 'asc'
                        )
                      }
                      sortDirection={header.column.getIsSorted()}
                    >
                      {TanstackTable.flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Table.Header>
                  ) : (
                    <Table.Header>
                      {TanstackTable.flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Table.Header>
                  )}
                </Fragment>
              ))}
              {<Table.Header key="action">Action</Table.Header>}
            </Table.HeaderRow>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row, index) => (
            <Table.Row
              key={row.id}
              rowIndex={index}
              selected={row.getIsSelected()}
              onSelectChange={(selected) => row.toggleSelected(selected)}
              className={row.getIsSelected() ? classes.active : ''}
            >
              {row.getVisibleCells().map((cell) => (
                <Table.Cell key={cell.id}>
                  {TanstackTable.flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
              {
                <Table.Cell key="action">
                  <div className={classes.actions}>
                    {renderEditForm && (
                      <FormModal
                        title="Edit Task"
                        renderForm={(onClose) => renderEditForm(onClose, row.original)}
                        renderButton={(onClick) => (
                          <Edit16
                            className={clsx({ [classes.disabled]: !hasUserAccess })}
                            onClick={hasUserAccess ? onClick : undefined}
                            title={hasUserAccess ? `Edit the task` : 'Edit is disabled'}
                          />
                        )}
                      />
                    )}
                  </div>
                </Table.Cell>
              }
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <PermitDetailsModal permit={selectedPermit} onClose={handleModalClose} />
    </div>
  );
}

export default JobTasksTable;
